// Set your APP_ID
export const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

// Check if we're in the browser and APP_ID is available
const isBrowser = typeof window !== "undefined";
const isAppIdValid = APP_ID && APP_ID !== "";

// TypeScript interfaces for Intercom
type IntercomAPI = {
  (...args: unknown[]): void;
  q: unknown[];
  c: (args: unknown) => void;
};

type IntercomOptions = {
  api_base?: string;
  app_id?: string;
  [key: string]: unknown;
};

// Type guard to check if Intercom is available
function hasIntercom(
  window: Window
): window is Window & { Intercom: IntercomAPI } {
  return (
    "Intercom" in window &&
    typeof (window as unknown as Record<string, unknown>).Intercom ===
      "function"
  );
}

// Loads Intercom with the snippet
// This must be run before boot, it initializes window.Intercom
export const load = (): void => {
  if (!(isBrowser && isAppIdValid)) {
    console.warn(
      "Intercom: Cannot load - either not in browser or APP_ID missing"
    );
    return;
  }

  // Check if Intercom is already loaded
  if (hasIntercom(window)) {
    return;
  }

  // Intercom loading script with proper TypeScript
  const windowWithIntercom = window as unknown as Record<string, unknown>;
  const ic = windowWithIntercom.Intercom as IntercomAPI | undefined;

  if (typeof ic === "function") {
    ic("reattach_activator");
    ic("update", windowWithIntercom.intercomSettings);
  } else {
    const d = document;
    const i = ((...args: unknown[]) => {
      (i as IntercomAPI).c(args);
    }) as IntercomAPI;

    i.q = [];
    i.c = (args: unknown) => {
      i.q.push(args);
    };

    windowWithIntercom.Intercom = i;

    const l = () => {
      const s = d.createElement("script");
      s.type = "text/javascript";
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${APP_ID}`;

      const x = d.getElementsByTagName("script")[0];
      if (x?.parentNode) {
        x.parentNode.insertBefore(s, x);
      }
    };

    if (document.readyState === "complete") {
      l();
    } else {
      const attachEvent = windowWithIntercom.attachEvent as
        | ((event: string, handler: () => void) => void)
        | undefined;
      if (attachEvent) {
        attachEvent("onload", l);
      } else {
        window.addEventListener("load", l, false);
      }
    }
  }
};

// Initializes Intercom
export const boot = (options: IntercomOptions = {}): void => {
  if (!(isBrowser && isAppIdValid)) {
    console.warn(
      "Intercom: Cannot boot - either not in browser or APP_ID missing"
    );
    return;
  }

  if (hasIntercom(window)) {
    const bootOptions = {
      api_base: "https://api-iam.intercom.io",
      app_id: APP_ID,
      ...options,
    };

    window.Intercom("boot", bootOptions);
  }
};

// Updates Intercom (call this on route changes)
export const update = (): void => {
  if (!isBrowser) {
    return;
  }

  if (hasIntercom(window)) {
    window.Intercom("update");
  }
};

// Shows the Intercom messenger
export const show = (): void => {
  if (!isBrowser) {
    return;
  }

  if (hasIntercom(window)) {
    window.Intercom("show");
  }
};

// Hides the Intercom messenger
export const hide = (): void => {
  if (!isBrowser) {
    return;
  }

  if (hasIntercom(window)) {
    window.Intercom("hide");
  }
};

// Shuts down Intercom (call this on logout)
export const shutdown = (): void => {
  if (!isBrowser) {
    return;
  }

  if (hasIntercom(window)) {
    window.Intercom("shutdown");
  }
};
